import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import { SelectField, TextField, ToggleSwitch } from "@/components/base/FormControls";
import { OFFER_DISCOUNT_TYPE_OPTIONS, OFFER_SCOPE_OPTIONS } from "@/lib/constants";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import type { Category, Offer, Product } from "@/types";

interface OfferFormModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  offer: Offer | null;
  products: Product[];
  categories: Category[];
}

interface OfferForm {
  name: string;
  scope: string;
  product_id: string;
  category_id: string;
  discount_type: string;
  discount_value: string;
  special_price: string;
  start_date: string;
  end_date: string;
  active: boolean;
}

const EMPTY: OfferForm = {
  name: "",
  scope: "product",
  product_id: "",
  category_id: "",
  discount_type: "percentage",
  discount_value: "",
  special_price: "",
  start_date: "",
  end_date: "",
  active: true,
};

function toNumber(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number.parseFloat(value.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

export default function OfferFormModal({
  open,
  onClose,
  onSaved,
  offer,
  products,
  categories,
}: OfferFormModalProps) {
  const toast = useToast();
  const { profile } = useAuth();
  const [form, setForm] = useState<OfferForm>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (offer) {
      setForm({
        name: offer.name,
        scope: offer.scope,
        product_id: offer.product_id ?? "",
        category_id: offer.category_id ?? "",
        discount_type: offer.discount_type,
        discount_value: offer.discount_value != null ? String(offer.discount_value) : "",
        special_price: offer.special_price != null ? String(offer.special_price) : "",
        start_date: offer.start_date ?? "",
        end_date: offer.end_date ?? "",
        active: offer.active ?? true,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, offer]);

  const update = <K extends keyof OfferForm>(key: K, value: OfferForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!form.name.trim()) {
      setError("El nombre de la oferta es obligatorio.");
      return;
    }
    if (form.scope === "product" && !form.product_id) {
      setError("Selecciona el producto de la oferta.");
      return;
    }
    if (form.scope === "category" && !form.category_id) {
      setError("Selecciona la categoría de la oferta.");
      return;
    }
    if (form.discount_type === "special_price") {
      if (toNumber(form.special_price) == null) {
        setError("Indica el precio especial.");
        return;
      }
    } else if (toNumber(form.discount_value) == null) {
      setError("Indica el valor del descuento.");
      return;
    }
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      setError("La fecha de fin no puede ser anterior a la de inicio.");
      return;
    }

    const payload = {
      name: form.name.trim(),
      scope: form.scope,
      product_id: form.scope === "product" ? form.product_id || null : null,
      category_id: form.scope === "category" ? form.category_id || null : null,
      discount_type: form.discount_type,
      discount_value: form.discount_type === "special_price" ? null : toNumber(form.discount_value),
      special_price: form.discount_type === "special_price" ? toNumber(form.special_price) : null,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      active: form.active,
      updated_at: new Date().toISOString(),
    };

    setSubmitting(true);
    try {
      if (offer) {
        const { error: updateError } = await supabase.from("offers").update(payload).eq("id", offer.id);
        if (updateError) {
          setError("No se pudo actualizar la oferta.");
          return;
        }
        await logAudit({
          userId: profile?.id ?? null,
          userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
          action: "editar",
          entity: "offers",
          entityId: offer.id,
          branchId: profile?.branch_id ?? null,
          oldValue: { nombre: offer.name, valor: offer.discount_value },
          newValue: { nombre: payload.name, valor: payload.discount_value ?? payload.special_price },
        });
        toast.success("Oferta actualizada.");
      } else {
        const { data, error: insertError } = await supabase
          .from("offers")
          .insert(payload)
          .select("id")
          .single();
        if (insertError || !data) {
          setError("No se pudo crear la oferta.");
          return;
        }
        await logAudit({
          userId: profile?.id ?? null,
          userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
          action: "crear",
          entity: "offers",
          entityId: data.id,
          branchId: profile?.branch_id ?? null,
          newValue: { nombre: payload.name, tipo: payload.discount_type },
        });
        toast.success("Oferta creada. El POS la aplicará automáticamente.");
      }
      onSaved();
      onClose();
    } catch {
      setError("Ocurrió un error inesperado. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={offer ? "Editar oferta" : "Nueva oferta"}
      subtitle="El POS mostrará y aplicará la oferta activa automáticamente según su vigencia."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="offer-form"
            disabled={submitting}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
          >
            {submitting ? <i className="ri-loader-4-line animate-spin text-base"></i> : null}
            {offer ? "Guardar cambios" : "Crear oferta"}
          </button>
        </>
      }
    >
      <form id="offer-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
        <TextField
          label="Nombre de la oferta"
          name="offer_name"
          value={form.name}
          onChange={(value) => update("name", value)}
          placeholder="Ej: Promoción de verano"
          required
        />

        <SelectField
          label="Alcance"
          name="offer_scope"
          value={form.scope}
          onChange={(value) => update("scope", value)}
          options={OFFER_SCOPE_OPTIONS}
          hint="Puedes descontar un producto específico o toda una categoría."
        />

        {form.scope === "product" ? (
          <SelectField
            label="Producto"
            name="offer_product"
            value={form.product_id}
            onChange={(value) => update("product_id", value)}
            placeholder="Selecciona un producto"
            required
            options={products.map((product) => ({
              value: product.id,
              label: product.sku ? `${product.name} · ${product.sku}` : product.name,
            }))}
          />
        ) : (
          <SelectField
            label="Categoría"
            name="offer_category"
            value={form.category_id}
            onChange={(value) => update("category_id", value)}
            placeholder="Selecciona una categoría"
            required
            options={categories.map((category) => ({ value: category.id, label: category.name }))}
          />
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectField
            label="Tipo de descuento"
            name="offer_discount_type"
            value={form.discount_type}
            onChange={(value) => update("discount_type", value)}
            options={OFFER_DISCOUNT_TYPE_OPTIONS}
          />
          {form.discount_type === "special_price" ? (
            <TextField
              label="Precio especial"
              name="offer_special_price"
              value={form.special_price}
              onChange={(value) => update("special_price", value)}
              inputMode="numeric"
              placeholder="0.00"
              required
            />
          ) : (
            <TextField
              label={form.discount_type === "percentage" ? "Descuento (%)" : "Monto (RD$)"}
              name="offer_discount_value"
              value={form.discount_value}
              onChange={(value) => update("discount_value", value)}
              inputMode="numeric"
              placeholder={form.discount_type === "percentage" ? "10" : "100.00"}
              required
            />
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Vigente desde"
            name="offer_start"
            type="date"
            value={form.start_date}
            onChange={(value) => update("start_date", value)}
            hint="Opcional. Sin fecha, aplica desde hoy."
          />
          <TextField
            label="Vigente hasta"
            name="offer_end"
            type="date"
            value={form.end_date}
            onChange={(value) => update("end_date", value)}
            hint="Opcional. Sin fecha, no vence."
          />
        </div>

        <ToggleSwitch
          label="Oferta activa"
          description="Si está activa y dentro de la vigencia, el POS la aplica automáticamente."
          checked={form.active}
          onChange={(checked) => update("active", checked)}
        />

        {error ? (
          <div className="animate-shake flex items-start gap-2.5 rounded-md border border-primary-300 bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
              <i className="ri-error-warning-line text-base"></i>
            </span>
            <span className="flex-1 leading-snug">{error}</span>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}